<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        List of users
      </span>
      <a-button size="large" class="a-header--button slide-down-enter-active" type="primary">
        <router-link :to="'./create-account'">
          <a-icon type="plus-square" theme="filled" />
          Add New
        </router-link>
      </a-button>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Search" @change="onSearch" />
            </div>
          </a-col>
          <a-col :span="8">
            <div class="full-w a-select-group" :class="{ active: filterParams.status.length > 0 }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select
                v-model="filterParams.status"
                mode="multiple"
                size="large"
                placeholder="Status"
                :maxTagCount="2"
                @change="filter"
              >
                <a-select-option v-for="(status, label) in UserStatus" :key="status" :value="status">
                  {{ label }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
        </a-row>
        <a-row class="full-w mt-2" type="flex" justify="start" align="middle">
          <a-tag color="blue">
            {{ selectedIds.length }} user(s) selected
          </a-tag>
          <a-button size="default" type="danger" class="ml-2 danger-btn" :disabled="!hasSelected" :loading="isDisabling" @click="onDisableSelected">
            <a-icon type="stop" />
            Disable
          </a-button>
          <a-button size="default" class="ml-2" :disabled="!hasSelected || isDisabling" @click="copyRbacVisible = true">
            <a-icon type="copy" />
            Copy RBAC from…
          </a-button>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable
            :pagination="pagin"
            :columns="UserListColumn"
            :dataSource="filterData"
            :tableSize="tableSize"
            :rowClickable="true"
            :hasSelectRow="true"
            @row-click="onRowClick"
            @changeSelectedRow="onSelectChange"
          >
            <template slot="userName" slot-scope="{ record }">
              <p class="d-block user-name">
                {{ record.userName }}
              </p>
            </template>
            <template slot="createDate" slot-scope="{ record }">
              <div>
                <p class="mb-0">
                  <time>{{ record.createDate | dateString(false) }}</time>
                  <br />
                  <time>{{ record.createDate | timeString(false) }}</time>
                  (UTC)
                </p>
              </div>
            </template>
            <template slot="expiryDate" slot-scope="{ record }">
              <div>
                <p class="mb-0">
                  <time>{{ record.expiryDate | dateString(false) }}</time>
                  <br />
                  <time>{{ record.expiryDate | timeString(false) }}</time>
                  (UTC)
                </p>
              </div>
            </template>
            <template slot="status" slot-scope="{ record }">
              <div
                class="tag text-capitalize"
                :class="{
                  'bg-success': record.status === UserStatus.Verified,
                  'bg-inactive': record.status !== UserStatus.Verified,
                }"
              >
                {{ record.status }}
              </div>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" @click.stop />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item>
                    <a-icon type="export" />
                    <router-link class="nav-link-detail" :to="'./' + record.customerId + '/account'">
                      Edit account details
                    </router-link>
                  </a-menu-item>
                  <a-menu-item>
                    <a-icon type="setting" />
                    <router-link class="nav-link-detail" :to="'./' + record.customerId + '/access'">
                      Edit access
                    </router-link>
                  </a-menu-item>
                  <a-menu-item>
                    <a-icon type="team" />
                    <router-link class="nav-link-detail" :to="'./' + record.customerId + '/rbac'">
                      Edit RBAC
                    </router-link>
                  </a-menu-item>
                  <!-- <a-menu-item @click="(e) => onApprove(e, record)">
                    <a-icon type="check-square" />
                    <span>Approve</span>
                  </a-menu-item>
                  <a-menu-item @click="(e) => onDeactivate(e, record)">
                    <a-icon type="close-square" />
                    <span>Deactivate</span>
                  </a-menu-item>
                  <a-menu-item @click="(e) => onDelete(e, record)">
                    <a-icon type="delete" />
                    <span>Delete</span>
                  </a-menu-item> -->
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :current="pagin.current" :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
    <CopyRbacModal :visible="copyRbacVisible" :targets="selectedUsers" @close="copyRbacVisible = false" @done="onCopyRbacDone" />
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import { isNavigationFailure } from 'vue-router';
  import { UserListColumn } from '../columnsTable/UserListColumn';
  import AuthenticationService from '@modules/Authentication/services/authentication.service';
  import { UserStatus } from '../consts/UserManagement.const';
  import UserManagementService from '../services/UserManagement.service';
  import { formatNames } from '../helpers/copyRbac';
  import { runSequentially } from '../helpers/runSequentially';
  import CopyRbacModal from './CopyRbacModal.vue';

  export default {
    name: 'UserManagementList',
    components: { CopyRbacModal },
    data() {
      var pageSize = Number(window.localStorage.getItem("pageSize"));
      if(!pageSize)
       pageSize = 5;

      return {
        UserStatus,
        UserListColumn,
        dataUsers: [],
        filterData: [],
        selectedIds: [],
        isDisabling: false,
        copyRbacVisible: false,
        filterParams: {
          search: '',
          status: [UserStatus.Verified],
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0,
        },
        isLoading: true,
        tableSize: {
          height: '',
          width: '',
        },
      };
    },
    computed: {
      hasSelected() {
        return this.selectedIds.length > 0;
      },
      selectedUsers() {
        const selectedIdSet = new Set(this.selectedIds);
        return this.dataUsers.filter(user => selectedIdSet.has(user.customerId));
      },
    },
    mounted() {
      this.fetchList();
    },
    methods: {
      onSearch: debounce(function (inputChangeEvent) {
        const inputSearchValue = inputChangeEvent.target.value;
        this.filterParams.search = inputSearchValue ? String(inputSearchValue).trim().toLowerCase() : '';
        this.filter();
      }, 800),
      async fetchList() {
        try {
          this.dataUsers = await UserManagementService.fetchListUsers();
          const presentIds = new Set(this.dataUsers.map(user => user.customerId));
          this.selectedIds = this.selectedIds.filter(id => presentIds.has(id));
          this.filter();
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      filter() {
        const { search, status } = this.filterParams;
        let filterResult = this.dataUsers;
        if (status.length) {
          filterResult = filterResult.filter(item => status.includes(String(item.status || '').toLowerCase()));
        }
        if (search) {
          filterResult = filterResult.filter(item => [item.userName, item.customerId, item.ownerId].some(value => (value || '').toString().toLowerCase().includes(search)));
        }
        const selectedIdSet = new Set(this.selectedIds);
        filterResult = filterResult.map((item, index) => ({ ...item, id: item.customerId, index: index + 1, isChecked: selectedIdSet.has(item.customerId) }));
        this.filterData = filterResult;
        this.pagin = { ...this.pagin, current: 1, total: filterResult.length };
      },
      paginChange(pagin) {
        this.pagin = { ...pagin };
      },
      elSizeChange(size) {
        this.tableSize = size;
      },
      onSelectChange(ids) {
        // The table reports every checked id in the filtered data, so keep selections hidden by the current filters.
        const shownIds = new Set(this.filterData.map(row => row.id));
        this.selectedIds = [...this.selectedIds.filter(id => !shownIds.has(id)), ...ids];
      },
      // Held from the click until the confirm is cancelled or the disable finishes, so a second click cannot open a second dialog.
      async onDisableSelected() {
        if (this.isDisabling) return;
        this.isDisabling = true;
        try {
          const protectedIds = await this.fetchProtectedIds();
          if (!protectedIds) {
            this.isDisabling = false;
            return;
          }
          const selectedUsers = this.dataUsers.filter(user => this.selectedIds.includes(user.customerId));
          const targets = selectedUsers.filter(user => !protectedIds.has(String(user.customerId)) && String(user.status || '').toLowerCase() !== UserStatus.Disabled);
          if (!targets.length) {
            this.isDisabling = false;
            this.$showInfoMessage('No users to disable.');
            return;
          }
          let hasStarted = false;
          this.$confirm({
            title: `Disable ${targets.length} user(s)?`,
            content: this.buildDisableConfirmContent(targets, selectedUsers.length - targets.length),
            okText: 'Disable',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
              hasStarted = true;
              return this.disableUsers(targets.map(user => user.customerId));
            },
            onCancel: () => {
              if (!hasStarted) {
                this.isDisabling = false;
              }
            },
          });
        } catch (error) {
          this.isDisabling = false;
          throw error;
        }
      },
      // The login account and the "viewing as" account must both survive a bulk disable.
      async fetchProtectedIds() {
        let loggedInUser;
        try {
          loggedInUser = await AuthenticationService.getUser();
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
          return null;
        }
        if (!loggedInUser || loggedInUser.id === null || loggedInUser.id === undefined) {
          this.$showErrorMessage('Could not determine your account. Try again.');
          return null;
        }
        const viewingAsId = (this.$store.state.user.data || {}).id;
        return new Set([loggedInUser.id, viewingAsId].filter(id => id !== null && id !== undefined).map(String));
      },
      buildDisableConfirmContent(targets, skippedCount) {
        const lines = [formatNames(targets.map(user => user.userName)), 'They will no longer be able to log in. Sessions that are already open stay active until they expire.'];
        if (skippedCount > 0) {
          lines.push(`${skippedCount} selected user(s) skipped: your own account or already disabled.`);
        }
        return this.$createElement(
          'div',
          lines.map(line => this.$createElement('p', line))
        );
      },
      async disableUsers(ids) {
        try {
          const { failedIds, reason, stoppedBy } = await runSequentially(ids, id => UserManagementService.updateUserById(id, { status: UserStatus.Disabled }));
          const disabledCount = ids.length - failedIds.length;
          const reasonSuffix = reason ? ` ${reason}` : '';
          if (!failedIds.length) {
            this.$showSuccessMessage(`Disabled ${disabledCount} user(s).`);
          } else if (!disabledCount) {
            this.$showErrorMessage(`Failed to disable the selected users.${reasonSuffix}`);
          } else {
            this.$showWarningMessage(`Disabled ${disabledCount} of ${ids.length} user(s); ${failedIds.length} failed. The failed users stay selected.${reasonSuffix}`);
          }
          this.selectedIds = failedIds;
          this.filter();
          // On 401 the interceptor has already redirected to /login.
          if (stoppedBy !== 401) {
            await this.fetchList();
          }
        } finally {
          this.isDisabling = false;
        }
      },
      async onCopyRbacDone({ failedIds, stoppedBy }) {
        this.copyRbacVisible = false;
        this.selectedIds = failedIds;
        this.filter();
        // On 401 the interceptor has already redirected to /login.
        if (stoppedBy !== 401) {
          await this.fetchList();
        }
      },
      onRowClick(record) {
        this.$router.push({ name: 'user-management-account', params: { id: record.customerId } }).catch(error => {
          if (!isNavigationFailure(error)) {
            throw error;
          }
        });
      },
      onApprove(event, record) {
        event.domEvent.stopPropagation();
        this.$showErrorMessage('Feature is coming');
      },
      onDeactivate(event, record) {
        event.domEvent.stopPropagation();
        this.$showErrorMessage('Feature is coming');
      },
      onDelete(event, record) {
        event.domEvent.stopPropagation();
        this.$showErrorMessage('Feature is coming');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .user-name {
    word-break: break-all;
    margin: 0;
  }
  .time-text {
    margin-bottom: 0;
    white-space: nowrap;
  }
  .nav-link-detail {
    width: 100%;
    &:hover {
      text-decoration: underline;
    }
  }
  .nav-link-detail {
    display: inline-block;
  }
  .custom-suffix {
    font-size: 20px;
    color: #20a4b8;
  }
  .danger-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #f5222d;
    }
  }
  .webpab-ext-link {
    color: inherit;
    display: block;
    width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover {
      color: #20a4b8;
      text-decoration: underline;
    }
  }
</style>
